import SEOHead from '@/components/SEOHead';
import { REFERRAL, REFERRAL_DISCOUNT, PAYOUT_MINIMUM_USD, percent, tierRanges } from '@/config/earnings';

// The programme's numbers come from builderslab/shared/earnings.json — the
// file the commission ledger reads — so these terms cannot drift from what is
// actually paid.
const LAST_UPDATED = 'October 19, 2026';
const CONTACT_EMAIL = 'affiliates@builderslab.net';
const COMMISSION_RATE = percent(Math.max(...REFERRAL.tiers.map(t => t.rate)));
const HOLD_DAYS = REFERRAL.holdDays;
const MIN_PAYOUT_USD = PAYOUT_MINIMUM_USD;

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="text-xl font-bold text-white mb-4 pb-3 border-b border-zinc-800">{title}</h2>
      <div className="space-y-4 text-zinc-400 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}

export default function AffiliateTnC() {
  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-20">
      <SEOHead
        title="Affiliate Terms & Conditions — BuildersLab"
        description="Read the full terms and conditions for the BuildersLab affiliate programme, including commission rates, payment schedule, and prohibited activities."
        ogUrl="https://builderslab.net/AffiliateTnC"
        noIndex={false}
      />
      <div className="max-w-4xl mx-auto px-6">

        <div
         
         
         
          className="mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold uppercase tracking-wider mb-6">
            Legal
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
            Affiliate Programme Terms &amp; Conditions
          </h1>
          <p className="text-zinc-500 text-sm">Last updated: {LAST_UPDATED}</p>
          <p className="text-zinc-400 mt-4 text-base leading-relaxed max-w-2xl">
            These terms govern participation in the BuildersLab Affiliate Programme. By applying or
            participating you confirm that you have read, understood, and agree to be bound by them.
          </p>
        </div>

        <div
         
         
         
        >
          <Section title="1. Programme Overview">
            <p>
              The BuildersLab Affiliate Programme allows account holders ("Affiliates") to earn
              commissions by referring paying subscribers to BuildersLab
              (<a href="https://builderslab.net" className="text-orange-400 hover:underline">builderslab.net</a>).
              Every BuildersLab account has a personal referral code and link, shown in the
              dashboard under Earn. Partners who applied through the affiliate application form
              and were approved may also hold a separate partner code, which works the same way.
            </p>
            <p>
              A customer who enters a valid referral code when buying a subscription plan receives a
              discount of <strong className="text-white">{REFERRAL_DISCOUNT}</strong> on that purchase.
              The discount applies to subscription plans only, not to credit packs or marketplace
              purchases, and cannot be combined with another code.
            </p>
            <p>
              We reserve the right to refuse, suspend, or terminate any affiliate's participation at
              any time.
            </p>
          </Section>

          <Section title="2. FTC Disclosure Requirement">
            <p className="text-amber-400 font-medium">
              All affiliates are required by law (including the US FTC 16 CFR Part 255, UK ASA CAP
              Code, and equivalent regulations in your jurisdiction) to clearly and conspicuously
              disclose their material relationship with BuildersLab whenever they promote our
              products or share their referral link.
            </p>
            <p>
              Acceptable disclosure language includes (but is not limited to):
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>"This is a paid partnership with BuildersLab."</li>
              <li>"I earn a commission if you sign up through this link."</li>
              <li>"#ad" or "#sponsored" — when used prominently, not buried in tags.</li>
            </ul>
            <p>
              Disclosure must appear <strong className="text-white">before</strong> the referral link,
              not after it, and must be visible without the user needing to expand or scroll. Failure
              to disclose is grounds for immediate termination and forfeiture of unpaid commissions.
            </p>
          </Section>

          <Section title="3. Commission Structure">
            <p>
              Affiliates earn a commission of up to{' '}
              <strong className="text-white">{COMMISSION_RATE}</strong> of the amount actually paid
              (after the referral discount) for every subscription purchase made with their code —
              including renewals, for which a referred customer's code is filled in automatically.
            </p>
            <p>
              The rate is set by the number of distinct customers who have paid for a plan with your
              code, counting the purchase being credited:
            </p>
            <div className="overflow-x-auto rounded-xl border border-zinc-800 mt-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 bg-zinc-900/60">
                    <th className="text-left py-3 px-4 text-zinc-300 font-semibold">Tier</th>
                    <th className="text-left py-3 px-4 text-zinc-300 font-semibold">Paying customers</th>
                    <th className="text-left py-3 px-4 text-zinc-300 font-semibold">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {tierRanges().map((t, i, all) => (
                    <tr key={t.name} className={i < all.length - 1 ? 'border-b border-zinc-800/50' : ''}>
                      <td className="py-3 px-4 text-white">{t.name}</td>
                      <td className="py-3 px-4 text-zinc-400">{t.range}</td>
                      <td className="py-3 px-4 text-orange-400 font-medium">{percent(t.rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-zinc-500 text-xs mt-2">
              Commissions are booked in US dollars. A payment made in another currency is converted at
              the rate in force when it is booked. A refunded or charged-back purchase voids its
              commission.
            </p>
          </Section>

          <Section title="4. Referral Tracking">
            <p>
              Referrals are tracked by code:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>A visitor who signs up through your referral link is linked to your account, and your code is filled in for them at checkout.</li>
              <li>Any customer may also enter your code by hand at checkout. The commission for a purchase goes to the owner of the code used for it.</li>
              <li>A customer's first referrer stays linked to their account; entering another code later credits that purchase to the other code's owner.</li>
              <li>Self-referrals (using your own code, or codes between accounts you control) are prohibited and void any commission.</li>
            </ul>
          </Section>

          <Section title="5. Payment Schedule">
            <p>
              You may request a payout from the dashboard (Earn) at any time, provided:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Your available balance meets the minimum payout threshold of <strong className="text-white">${MIN_PAYOUT_USD} USD</strong>.</li>
              <li>Your payment details and tax information (W-9 for US residents; W-8BEN for international) are on file and validated.</li>
              <li>Your account is in good standing (no policy violations under review).</li>
            </ul>
            <p>
              Payments are made by bank transfer to the account you give in the dashboard, usually within a few working days of a request. BuildersLab is not
              responsible for delays caused by payment processors, banks, or incorrect payment details
              provided by the affiliate.
            </p>
            <p>
              BuildersLab reserves a <strong className="text-white">{HOLD_DAYS}-day chargeback window</strong>.
              Each commission is held for {HOLD_DAYS} days after the purchase it was earned on, to
              account for refunds or chargebacks, and becomes available to withdraw after that.
            </p>
          </Section>

          <Section title="6. Prohibited Activities">
            <p>
              The following activities will result in immediate termination and forfeiture of unpaid
              commissions:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-white">Cookie stuffing</strong> — artificially inflating clicks by loading referral links without genuine user interaction.</li>
              <li><strong className="text-white">Misleading claims</strong> — making false or exaggerated claims about BuildersLab's features, pricing, or capabilities.</li>
              <li><strong className="text-white">Brand bidding</strong> — bidding on "BuildersLab" or confusingly similar keywords in paid search without prior written consent.</li>
              <li><strong className="text-white">Spam</strong> — distributing your referral link via unsolicited email, SMS, or messaging platforms in violation of applicable anti-spam laws (CAN-SPAM, CASL, GDPR).</li>
              <li><strong className="text-white">Self-referral</strong> — signing up through your own affiliate link.</li>
              <li><strong className="text-white">Fake traffic</strong> — using bots, click farms, or any artificial means to generate referrals.</li>
              <li><strong className="text-white">Disparaging competitors</strong> — making false or defamatory statements about competing products.</li>
              <li><strong className="text-white">Content prohibited by our AUP</strong> — promoting BuildersLab on websites or channels that contain adult content, hate speech, illegal material, or anything prohibited by our{' '}
                <a href="/AcceptableUsePolicy" className="text-orange-400 hover:underline">Acceptable Use Policy</a>.
              </li>
            </ul>
          </Section>

          <Section title="7. Intellectual Property">
            <p>
              BuildersLab grants you a non-exclusive, revocable, non-sublicensable licence to use
              our approved logos, screenshots, and marketing assets solely for promoting the
              programme in compliance with these terms. You may not alter, distort, or create
              derivative works of our brand assets without written consent.
            </p>
          </Section>

          <Section title="8. Termination">
            <p>
              Either party may terminate the affiliate relationship at any time with no notice
              required. Upon termination:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>All referral links must be removed from your properties.</li>
              <li>Commissions already earned (outside the {HOLD_DAYS}-day chargeback window) remain payable on request.</li>
              <li>If termination is due to a policy violation, all unpaid commissions are forfeited.</li>
            </ul>
          </Section>

          <Section title="9. Relationship of the Parties">
            <p>
              Affiliates are independent contractors. Nothing in these terms creates an employment,
              partnership, joint venture, or agency relationship. Affiliates are solely responsible
              for their own taxes and comply with all applicable laws in their jurisdiction.
            </p>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>
              BuildersLab's total liability to any affiliate under this programme shall not exceed
              the total commissions paid to that affiliate in the three months preceding the claim.
              We are not liable for indirect, incidental, or consequential damages.
            </p>
          </Section>

          <Section title="11. Modifications">
            <p>
              BuildersLab may amend these terms at any time by posting a revised version to this
              page and updating the "Last updated" date. Continued participation after a change
              constitutes acceptance of the new terms. We will notify active affiliates of material
              changes by email at least 14 days in advance where practicable.
            </p>
          </Section>

          <Section title="12. Governing Law">
            <p>
              These terms are governed by and construed in accordance with the laws of England and
              Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of
              England and Wales.
            </p>
          </Section>

          <Section title="13. Contact">
            <p>
              Questions about the affiliate programme or these terms:{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-orange-400 hover:underline">
                {CONTACT_EMAIL}
              </a>
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
